import { useState } from "react";
import { format, addDays, startOfWeek, addHours, parseISO } from "date-fns";
import {
  CalendarDays, Plus, ChevronLeft, ChevronRight, Clock,
  Trash2, Edit, DollarSign, CheckCircle2, XCircle, CreditCard,
  MapPin, Users, Settings, ExternalLink,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { format as formatDate } from "date-fns";
import {
  useAmenities, useAmenityBookings,
  useCreateAmenityBooking, useCancelAmenityBooking,
  usePendingAmenityBookings, useApproveAmenityBooking, useRejectAmenityBooking,
  useManageAmenity, useDeleteAmenity, calcBookingPrice,
  type AmenityBooking, type Amenity,
} from "@/hooks/useAmenities";
import { useCreateAmenityPayment } from "@/hooks/useStripeConnect";
import { useAuth } from "@/contexts/AuthContext";
import { usePmPermissionCheck } from "@/hooks/usePmPermissions";
import { useNavigate } from "react-router-dom";

const HOURS = Array.from({ length: 16 }, (_, i) => i + 7); // 7am–10pm

const COLORS = [
  "#4f46e5", "#0ea5e9", "#10b981", "#f59e0b",
  "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4",
];

const EMPTY_AMENITY = {
  name: "",
  description: "",
  capacity: "10",
  max_hours: "4",
  color: "#4f46e5",
  location: "",
  rules: "",
  requires_payment: false,
  require_approval: true,
  price_per_hour: "",
  price_flat: "",
};

function PaymentBadge({ paymentStatus }: { paymentStatus?: string }) {
  if (!paymentStatus || paymentStatus === "waived") return null;
  const map: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    unpaid:   { label: "Unpaid",   variant: "secondary" },
    pending:  { label: "Pay Now",  variant: "default" },
    paid:     { label: "Paid ✓",   variant: "outline" },
    refunded: { label: "Refunded", variant: "secondary" },
    failed:   { label: "Failed",   variant: "destructive" },
  };
  const cfg = map[paymentStatus] ?? { label: paymentStatus, variant: "secondary" as const };
  return <Badge variant={cfg.variant} className="text-[10px]">{cfg.label}</Badge>;
}

export default function Amenities() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const checkPerm = usePmPermissionCheck();

  const canManageAmenities =
    user?.role === "strata_admin" ||
    user?.role === "property_manager" ||
    user?.role === "host" ||
    user?.role === "system_admin";

  const canApproveBookings =
    (user?.role === "strata_admin" ||
      user?.role === "property_manager" ||
      user?.role === "system_admin") &&
    checkPerm("approve_amenity_bookings");

  // ── State ──────────────────────────────────────────────────────────────────

  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 }),
  );
  const [selectedAmenityId, setSelectedAmenityId] = useState<string | null>(null);

  // Book dialog
  const [showBookDialog, setShowBookDialog] = useState(false);
  const [newBooking, setNewBooking] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    startHour: "10",
    durationHours: "1",
    title: "",
    notes: "",
    guest_count: "1",
  });

  // Create/Edit amenity dialog
  const [showAmenityDialog, setShowAmenityDialog] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState<Amenity | null>(null);
  const [amenityForm, setAmenityForm] = useState(EMPTY_AMENITY);

  // Reject dialog
  const [rejectTarget, setRejectTarget] = useState<AmenityBooking | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  // Payment redirect
  const [paymentBooking, setPaymentBooking] = useState<AmenityBooking | null>(null);

  // ── Data ───────────────────────────────────────────────────────────────────

  const { data: amenities = [], isLoading: amenitiesLoading } = useAmenities();
  const { data: pendingBookings = [] } = usePendingAmenityBookings();

  const weekEnd = addDays(weekStart, 6);
  const activeAmenity =
    amenities.find((a) => a.id === selectedAmenityId) ?? amenities[0];

  const { data: bookings = [], isLoading: bookingsLoading } = useAmenityBookings(
    activeAmenity?.id,
    weekStart.toISOString(),
    addDays(weekEnd, 1).toISOString(),
  );

  const createBooking = useCreateAmenityBooking();
  const cancelBooking = useCancelAmenityBooking();
  const approveBooking = useApproveAmenityBooking();
  const rejectBooking = useRejectAmenityBooking();
  const manageAmenity = useManageAmenity();
  const deleteAmenity = useDeleteAmenity();
  const createPayment = useCreateAmenityPayment();

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // ── Helpers ────────────────────────────────────────────────────────────────

  function getBookingsForSlot(day: Date, hour: number): AmenityBooking[] {
    return bookings.filter((b) => {
      const start = parseISO(b.start_time);
      const end = parseISO(b.end_time);
      const slotStart = new Date(day);
      slotStart.setHours(hour, 0, 0, 0);
      const slotEnd = addHours(slotStart, 1);
      return (
        start < slotEnd &&
        end > slotStart &&
        format(start, "yyyy-MM-dd") === format(day, "yyyy-MM-dd")
      );
    });
  }

  function openEditAmenity(a: Amenity) {
    setEditingAmenity(a);
    setAmenityForm({
      name: a.name,
      description: a.description ?? "",
      capacity: String(a.capacity),
      max_hours: String(a.max_hours),
      color: a.color,
      location: a.location ?? "",
      rules: a.rules ?? "",
      requires_payment: a.requires_payment,
      require_approval: a.require_approval,
      price_per_hour: a.price_per_hour != null ? String(a.price_per_hour) : "",
      price_flat: a.price_flat != null ? String(a.price_flat) : "",
    });
    setShowAmenityDialog(true);
  }

  function openNewAmenity() {
    setEditingAmenity(null);
    setAmenityForm(EMPTY_AMENITY);
    setShowAmenityDialog(true);
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  async function handleBook() {
    if (!activeAmenity) return;
    const start = new Date(
      `${newBooking.date}T${newBooking.startHour.padStart(2, "0")}:00:00`,
    );
    const end = addHours(start, parseInt(newBooking.durationHours));

    const result = await createBooking.mutateAsync({
      amenity_id: activeAmenity.id,
      title: newBooking.title || "Booking",
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      guest_count: parseInt(newBooking.guest_count),
      notes: newBooking.notes || undefined,
    });

    setShowBookDialog(false);
    setNewBooking({
      date: format(new Date(), "yyyy-MM-dd"),
      startHour: "10",
      durationHours: "1",
      title: "",
      notes: "",
      guest_count: "1",
    });

    // If payment is needed, redirect to Stripe Checkout
    if (result?.payment_status === "pending" && activeAmenity.requires_payment) {
      setPaymentBooking(result);
    }
  }

  async function handlePayNow(booking: AmenityBooking) {
    const result = await createPayment.mutateAsync({
      booking_id: booking.id,
      mode: "checkout",
      success_url: `${window.location.origin}/amenities?payment=success`,
      cancel_url: `${window.location.origin}/amenities?payment=cancelled`,
    });
    if (result.url) {
      window.location.href = result.url;
    }
  }

  async function handleSaveAmenity() {
    await manageAmenity.mutateAsync({
      id: editingAmenity?.id,
      name: amenityForm.name,
      description: amenityForm.description || null,
      capacity: parseInt(amenityForm.capacity) || 10,
      max_hours: parseInt(amenityForm.max_hours) || 4,
      color: amenityForm.color,
      location: amenityForm.location || null,
      rules: amenityForm.rules || null,
      requires_payment: amenityForm.requires_payment,
      require_approval: amenityForm.require_approval,
      price_per_hour:
        amenityForm.requires_payment && amenityForm.price_per_hour
          ? parseFloat(amenityForm.price_per_hour)
          : null,
      price_flat:
        amenityForm.requires_payment && amenityForm.price_flat
          ? parseFloat(amenityForm.price_flat)
          : null,
      is_active: true,
    });
    setShowAmenityDialog(false);
  }

  // ── Preview price for booking form ────────────────────────────────────────

  const previewPrice =
    activeAmenity?.requires_payment
      ? calcBookingPrice(
          activeAmenity,
          new Date(
            `${newBooking.date}T${newBooking.startHour.padStart(2, "0")}:00:00`,
          ).toISOString(),
          addHours(
            new Date(`${newBooking.date}T${newBooking.startHour.padStart(2, "0")}:00:00`),
            parseInt(newBooking.durationHours),
          ).toISOString(),
        )
      : 0;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Amenities</h1>
          <p className="text-sm text-muted-foreground">Book shared building amenities</p>
        </div>
        <div className="flex gap-2">
          {canManageAmenities && (
            <Button size="sm" variant="outline" onClick={() => navigate("/amenity-management")}>
              <Settings className="mr-1.5 h-3.5 w-3.5" /> Manage
            </Button>
          )}
          <Button size="sm" onClick={() => setShowBookDialog(true)} disabled={!activeAmenity}>
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Book Amenity
          </Button>
        </div>
      </div>

      {/* Payment success/cancel banner */}
      {new URLSearchParams(window.location.search).get("payment") === "success" && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Payment successful! Your booking has been submitted.
          </AlertDescription>
        </Alert>
      )}

      {/* Pending payment banner */}
      {paymentBooking && (
        <Alert className="border-amber-200 bg-amber-50">
          <CreditCard className="h-4 w-4 text-amber-700" />
          <AlertDescription className="text-amber-800 flex items-center justify-between">
            <span>
              Your booking requires payment of{" "}
              <strong>${paymentBooking.payment_amount.toFixed(2)} CAD</strong> to be confirmed.
            </span>
            <Button
              size="sm"
              className="ml-4 shrink-0"
              onClick={() => handlePayNow(paymentBooking)}
              disabled={createPayment.isPending}
            >
              <CreditCard className="mr-1 h-3 w-3" />
              {createPayment.isPending ? "Loading…" : "Pay Now"}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Pending Approvals (Admin/PM/Host) */}
      {(canApproveBookings || user?.role === "host") && pendingBookings.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-800">
              <Clock className="h-4 w-4" />
              Pending Approval ({pendingBookings.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingBookings.map((b) => (
              <div
                key={b.id}
                className="flex items-center justify-between gap-3 rounded-md bg-white border border-amber-100 px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-xs font-semibold truncate">{b.title}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {b.amenity?.name} ·{" "}
                    {formatDate(parseISO(b.start_time), "MMM d, h:mm a")} –{" "}
                    {formatDate(parseISO(b.end_time), "h:mm a")}
                    {b.booker?.full_name && ` · ${b.booker.full_name}`}
                    {b.guest_count > 1 && ` · ${b.guest_count} guests`}
                  </p>
                  <div className="mt-0.5 flex items-center gap-1.5">
                    <PaymentBadge paymentStatus={b.payment_status} />
                    {b.payment_amount > 0 && (
                      <span className="text-[10px] text-muted-foreground">
                        ${b.payment_amount.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs text-green-700 border-green-300 hover:bg-green-50"
                    onClick={() => approveBooking.mutate(b.id)}
                    disabled={approveBooking.isPending}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs text-red-700 border-red-300 hover:bg-red-50"
                    onClick={() => { setRejectTarget(b); setRejectReason(""); }}
                    disabled={rejectBooking.isPending}
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1" /> Deny
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Amenity selector */}
      {amenitiesLoading ? (
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-9 w-28" />
          ))}
        </div>
      ) : amenities.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12">
            <CalendarDays className="h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              No amenities configured.{" "}
              {canManageAmenities
                ? "Click \"Manage\" to add your first amenity."
                : "Ask your strata manager to add amenities."}
            </p>
            {canManageAmenities && (
              <Button size="sm" onClick={() => navigate("/amenity-management")}>
                <Plus className="mr-1 h-4 w-4" /> Add Amenity
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Amenity chips */}
          <div className="flex flex-wrap gap-2">
            {amenities.map((a) => (
              <button
                key={a.id}
                onClick={() => setSelectedAmenityId(a.id)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                  (selectedAmenityId ?? amenities[0]?.id) === a.id
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background border-border hover:border-primary/50"
                }`}
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: a.color }}
                />
                {a.name}
                {a.capacity > 0 && (
                  <span className="opacity-70">· {a.capacity} max</span>
                )}
                {a.requires_payment && a.price_per_hour != null && (
                  <span className="opacity-70">· ${a.price_per_hour}/hr</span>
                )}
                {a.requires_payment && a.price_flat != null && (
                  <span className="opacity-70">· ${a.price_flat} flat</span>
                )}
              </button>
            ))}
          </div>

          {/* Active amenity info */}
          {activeAmenity && (
            <Card className="border-0 bg-muted/40">
              <CardContent className="py-3 px-4">
                <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                  {activeAmenity.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {activeAmenity.location}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" /> Max {activeAmenity.capacity} guests
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Up to {activeAmenity.max_hours} hours
                  </span>
                  {activeAmenity.requires_payment && (
                    <span className="flex items-center gap-1 text-emerald-700 font-medium">
                      <DollarSign className="h-3 w-3" />
                      {activeAmenity.price_flat != null
                        ? `$${activeAmenity.price_flat} flat fee`
                        : activeAmenity.price_per_hour != null
                        ? `$${activeAmenity.price_per_hour}/hr`
                        : "Paid"}
                    </span>
                  )}
                  {!activeAmenity.requires_payment && (
                    <Badge variant="outline" className="text-[10px] px-1.5">Free</Badge>
                  )}
                  {activeAmenity.require_approval ? (
                    <Badge variant="secondary" className="text-[10px] px-1.5">Requires approval</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] px-1.5 border-green-300 text-green-700">Auto-approved</Badge>
                  )}
                  {canManageAmenities && (
                    <button
                      onClick={() => openEditAmenity(activeAmenity)}
                      className="flex items-center gap-1 text-primary hover:underline"
                    >
                      <Edit className="h-3 w-3" /> Edit
                    </button>
                  )}
                </div>
                {activeAmenity.description && (
                  <p className="text-xs text-muted-foreground mt-1.5">{activeAmenity.description}</p>
                )}
                {activeAmenity.rules && (
                  <p className="text-xs text-amber-700 mt-1.5 font-medium">📋 Rules: {activeAmenity.rules}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Week calendar */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">
                  {activeAmenity?.name ?? "Amenity"} Schedule
                </CardTitle>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setWeekStart((w) => addDays(w, -7))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-muted-foreground px-1">
                    {format(weekStart, "MMM d")} – {format(weekEnd, "MMM d")}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setWeekStart((w) => addDays(w, 7))}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {bookingsLoading ? (
                <Skeleton className="h-64 w-full" />
              ) : (
                <table className="w-full text-xs border-collapse min-w-[600px]">
                  <thead>
                    <tr>
                      <th className="w-14 text-muted-foreground font-normal py-1 pr-2 text-right">
                        Time
                      </th>
                      {weekDays.map((d) => (
                        <th
                          key={d.toISOString()}
                          className={`text-center py-1 px-1 font-medium ${
                            format(d, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd")
                              ? "text-primary"
                              : "text-foreground"
                          }`}
                        >
                          <div>{format(d, "EEE")}</div>
                          <div
                            className={`text-[11px] rounded-full w-6 h-6 flex items-center justify-center mx-auto ${
                              format(d, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd")
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground"
                            }`}
                          >
                            {format(d, "d")}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {HOURS.map((hour) => (
                      <tr key={hour} className="border-t border-border/50">
                        <td className="text-right pr-2 text-muted-foreground py-1 align-top">
                          {hour === 12
                            ? "12pm"
                            : hour < 12
                            ? `${hour}am`
                            : `${hour - 12}pm`}
                        </td>
                        {weekDays.map((day) => {
                          const slotBookings = getBookingsForSlot(day, hour);
                          const isPast = new Date(day).setHours(hour) < Date.now();
                          return (
                            <td
                              key={day.toISOString()}
                              className={`px-0.5 py-0.5 align-top ${isPast ? "opacity-40" : ""}`}
                            >
                              {slotBookings.length > 0
                                ? slotBookings.map((b) => (
                                    <div
                                      key={b.id}
                                      className="rounded text-[10px] px-1 py-0.5 leading-tight truncate"
                                      style={{
                                        backgroundColor: `${activeAmenity?.color}22`,
                                        borderLeft: `3px solid ${activeAmenity?.color ?? "#4f46e5"}`,
                                      }}
                                      title={b.title}
                                    >
                                      <span className="font-medium">{b.title}</span>
                                      {b.payment_status === "paid" && (
                                        <span className="ml-1 text-green-600">✓</span>
                                      )}
                                      {b.user_id === user?.id && (
                                        <button
                                          className="ml-1 text-muted-foreground hover:text-destructive"
                                          onClick={() => cancelBooking.mutate(b.id)}
                                        >
                                          ×
                                        </button>
                                      )}
                                    </div>
                                  ))
                                : <div className="h-6" />}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ── Book Dialog ────────────────────────────────────────────────────── */}
      <Dialog open={showBookDialog} onOpenChange={setShowBookDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Book {activeAmenity?.name ?? "Amenity"}</DialogTitle>
            {activeAmenity?.requires_payment && (
              <DialogDescription>
                {activeAmenity.price_flat != null
                  ? `Flat fee: $${activeAmenity.price_flat} CAD`
                  : `$${activeAmenity.price_per_hour}/hr — payment required`}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Title / Purpose</Label>
              <Input
                className="mt-1"
                placeholder="e.g. Birthday party"
                value={newBooking.title}
                onChange={(e) => setNewBooking((p) => ({ ...p, title: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date</Label>
                <Input
                  type="date"
                  className="mt-1"
                  value={newBooking.date}
                  onChange={(e) => setNewBooking((p) => ({ ...p, date: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Start Time</Label>
                <Select
                  value={newBooking.startHour}
                  onValueChange={(v) => setNewBooking((p) => ({ ...p, startHour: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map((h) => (
                      <SelectItem key={h} value={String(h)}>
                        {h === 12 ? "12:00 pm" : h < 12 ? `${h}:00 am` : `${h - 12}:00 pm`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Duration (hours)</Label>
                <Select
                  value={newBooking.durationHours}
                  onValueChange={(v) => setNewBooking((p) => ({ ...p, durationHours: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: activeAmenity?.max_hours ?? 4 }, (_, i) => i + 1).map((h) => (
                      <SelectItem key={h} value={String(h)}>
                        {h} hour{h > 1 ? "s" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Guests</Label>
                <Input
                  type="number"
                  min={1}
                  max={activeAmenity?.capacity ?? 50}
                  className="mt-1"
                  value={newBooking.guest_count}
                  onChange={(e) => setNewBooking((p) => ({ ...p, guest_count: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea
                className="mt-1"
                rows={2}
                value={newBooking.notes}
                onChange={(e) => setNewBooking((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>

            {/* Price preview */}
            {activeAmenity?.requires_payment && previewPrice > 0 && (
              <>
                <Separator />
                <div className="flex items-center justify-between rounded-md bg-muted px-3 py-2">
                  <span className="text-xs text-muted-foreground">Total due</span>
                  <span className="text-sm font-bold">${previewPrice.toFixed(2)} CAD</span>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  You will be redirected to Stripe's secure payment page to complete payment.
                  Money goes directly to the property manager's bank account.
                </p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBook}
              disabled={createBooking.isPending || !newBooking.date}
            >
              {createBooking.isPending
                ? "Booking…"
                : activeAmenity?.requires_payment && previewPrice > 0
                ? `Book & Pay $${previewPrice.toFixed(2)}`
                : "Confirm Booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Create / Edit Amenity Dialog ──────────────────────────────────── */}
      <Dialog open={showAmenityDialog} onOpenChange={setShowAmenityDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAmenity ? "Edit Amenity" : "Add New Amenity"}
            </DialogTitle>
            <DialogDescription>
              Configure this amenity for your building. Guests and tenants will be able to book it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Amenity Name *</Label>
                <Input
                  className="mt-1"
                  placeholder="e.g. Rooftop Terrace, Pool, Gym"
                  value={amenityForm.name}
                  onChange={(e) => setAmenityForm((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Location</Label>
                <Input
                  className="mt-1"
                  placeholder="e.g. 3rd floor, Rooftop"
                  value={amenityForm.location}
                  onChange={(e) => setAmenityForm((p) => ({ ...p, location: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Max Capacity</Label>
                <Input
                  type="number"
                  min={1}
                  className="mt-1"
                  value={amenityForm.capacity}
                  onChange={(e) => setAmenityForm((p) => ({ ...p, capacity: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Description</Label>
              <Textarea
                className="mt-1"
                rows={2}
                placeholder="Brief description of this amenity"
                value={amenityForm.description}
                onChange={(e) => setAmenityForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Max Booking Duration (hours)</Label>
                <Input
                  type="number"
                  min={1}
                  max={24}
                  className="mt-1"
                  value={amenityForm.max_hours}
                  onChange={(e) => setAmenityForm((p) => ({ ...p, max_hours: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Calendar Color</Label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setAmenityForm((p) => ({ ...p, color: c }))}
                      className={`w-6 h-6 rounded-full border-2 transition-all ${
                        amenityForm.color === c ? "border-foreground scale-110" : "border-transparent"
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs">Rules / Notes for guests (optional)</Label>
              <Textarea
                className="mt-1"
                rows={2}
                placeholder="e.g. No smoking, clean up after use, max noise level..."
                value={amenityForm.rules}
                onChange={(e) => setAmenityForm((p) => ({ ...p, rules: e.target.value }))}
              />
            </div>

            <Separator />

            {/* Booking settings */}
            <div className="space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Booking Settings
              </p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Require manual approval</p>
                  <p className="text-xs text-muted-foreground">
                    You review and approve each booking request
                  </p>
                </div>
                <Switch
                  checked={amenityForm.require_approval}
                  onCheckedChange={(v) => setAmenityForm((p) => ({ ...p, require_approval: v }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Charge a booking fee</p>
                  <p className="text-xs text-muted-foreground">
                    Guests pay via Stripe; funds go to your bank account
                  </p>
                </div>
                <Switch
                  checked={amenityForm.requires_payment}
                  onCheckedChange={(v) =>
                    setAmenityForm((p) => ({ ...p, requires_payment: v }))
                  }
                />
              </div>

              {amenityForm.requires_payment && (
                <div className="pl-4 border-l-2 border-primary/20 space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Set one pricing type. Leave the other blank.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Price per hour (CAD)</Label>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        placeholder="e.g. 25.00"
                        className="mt-1"
                        value={amenityForm.price_per_hour}
                        onChange={(e) =>
                          setAmenityForm((p) => ({
                            ...p,
                            price_per_hour: e.target.value,
                            price_flat: "",
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Flat booking fee (CAD)</Label>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        placeholder="e.g. 50.00"
                        className="mt-1"
                        value={amenityForm.price_flat}
                        onChange={(e) =>
                          setAmenityForm((p) => ({
                            ...p,
                            price_flat: e.target.value,
                            price_per_hour: "",
                          }))
                        }
                      />
                    </div>
                  </div>
                  <Alert className="border-blue-200 bg-blue-50 py-2">
                    <DollarSign className="h-3.5 w-3.5 text-blue-700" />
                    <AlertDescription className="text-xs text-blue-800">
                      To receive payments, your organization must connect a bank account in{" "}
                      <button
                        className="underline font-medium"
                        onClick={() => {
                          setShowAmenityDialog(false);
                          navigate("/amenity-management");
                        }}
                      >
                        Amenity Management → Payout Settings
                      </button>
                      .
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAmenityDialog(false)}>
              Cancel
            </Button>
            {editingAmenity && (
              <Button
                variant="destructive"
                onClick={async () => {
                  await deleteAmenity.mutateAsync(editingAmenity.id);
                  setShowAmenityDialog(false);
                }}
                disabled={deleteAmenity.isPending}
              >
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Remove
              </Button>
            )}
            <Button
              onClick={handleSaveAmenity}
              disabled={manageAmenity.isPending || !amenityForm.name.trim()}
            >
              {manageAmenity.isPending
                ? "Saving…"
                : editingAmenity
                ? "Save Changes"
                : "Create Amenity"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Reject Reason Dialog ──────────────────────────────────────────── */}
      <Dialog
        open={!!rejectTarget}
        onOpenChange={(v) => {
          if (!v) setRejectTarget(null);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Deny Booking</DialogTitle>
            <DialogDescription>
              Provide a reason for denying this booking (optional, but helpful for the guest).
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            placeholder="e.g. Conflicting event already scheduled..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={rejectBooking.isPending}
              onClick={async () => {
                if (rejectTarget) {
                  await rejectBooking.mutateAsync({
                    id: rejectTarget.id,
                    reason: rejectReason || undefined,
                  });
                  setRejectTarget(null);
                }
              }}
            >
              Confirm Denial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
